'use client';

import { useCallback, useContext, useReducer } from 'react';

import useFeatureToggle from '@guestyci/feature-toggle-fe/useFeatureToggle';

import { PRICE_FILTER_FIX } from 'constants/featureToggleNames';
import { CurrencyContext } from 'context/CurrencyContext';
import useSearchValues from './useSearchValues';

export const SEARCH_VALUES_ACTIONS = {
  SET_START_DATE: 'SET_START_DATE',
  SET_END_DATE: 'SET_END_DATE',
  SET_DATE_RANGE: 'SET_DATE_RANGE',
  SET_MIN_PRICE: 'SET_MIN_PRICE',
  SET_MAX_PRICE: 'SET_MAX_PRICE',
  SET_PROPERTY_TYPE: 'SET_PROPERTY_TYPE',
  SET_TAGS: 'SET_TAGS',
  SET_AMENITIES: 'SET_AMENITIES',
  SET_NUMBER_OF_BEDROOMS: 'SET_NUMBER_OF_BEDROOMS',
  SET_NUMBER_OF_BATHROOMS: 'SET_NUMBER_OF_BATHROOMS',
  SET_SELECTED_LOCATION: 'SET_SELECTED_LOCATION',
  SET_SELECTED_GUESTS_COUNT: 'SET_SELECTED_GUESTS_COUNT',
  SET_SELECTED_ROOMS_COUNT: 'SET_SELECTED_ROOMS_COUNT',
  RESET_PRICE: 'RESET_PRICE',
  RESET_PROPERTY_TYPE: 'RESET_PROPERTY_TYPE',
  RESET_TAGS: 'RESET_TAGS',
  RESET_AMENITIES: 'RESET_AMENITIES',
  RESET_NUMBER_OF_BEDROOMS: 'RESET_NUMBER_OF_BEDROOMS',
  RESET_NUMBER_OF_BATHROOMS: 'RESET_NUMBER_OF_BATHROOMS',
  CLEAR_ALL: 'CLEAR_ALL',
};

const initialState = (searchParams) => ({
  startDate: searchParams.startDate,
  endDate: searchParams.endDate,
  dateRange: { startDate: searchParams.startDate, endDate: searchParams.endDate },
  minPrice: searchParams.minPrice || null,
  maxPrice: searchParams.maxPrice || null,
  propertyType: searchParams.getParsedPropertyTypes(),
  tags: searchParams.getParsedTags(),
  amenities: searchParams.getAmenitiesAndHouseRules(),
  numberOfBedrooms: searchParams.numberOfBedrooms || 0,
  numberOfBathrooms: searchParams.numberOfBathrooms || 0,
  selectedLocation: {
    city: searchParams.city,
    country: searchParams.country,
    lat: searchParams.lat,
    lng: searchParams.lng,
  },
  sortBy: searchParams.sortBy,
  sortOrder: searchParams.sortOrder,
  selectedGuestsCount: searchParams.minOccupancy || 1,
  selectedRoomsCount: searchParams.rooms || 1,
});

function reducer(state, action) {
  switch (action.type) {
    case SEARCH_VALUES_ACTIONS.SET_START_DATE:
      return { ...state, startDate: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_END_DATE:
      return { ...state, endDate: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_DATE_RANGE:
      return { ...state, dateRange: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_MIN_PRICE:
      return { ...state, minPrice: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_MAX_PRICE:
      return { ...state, maxPrice: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_PROPERTY_TYPE:
      return { ...state, propertyType: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_TAGS:
      return { ...state, tags: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_AMENITIES:
      return { ...state, amenities: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_NUMBER_OF_BEDROOMS:
      return { ...state, numberOfBedrooms: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_NUMBER_OF_BATHROOMS:
      return { ...state, numberOfBathrooms: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_SELECTED_LOCATION:
      return { ...state, selectedLocation: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_SELECTED_GUESTS_COUNT:
      return { ...state, selectedGuestsCount: action.payload };
    case SEARCH_VALUES_ACTIONS.SET_SELECTED_ROOMS_COUNT:
      return { ...state, selectedRoomsCount: action.payload };
    case SEARCH_VALUES_ACTIONS.RESET_PRICE:
      return { ...state, minPrice: null, maxPrice: null };
    case SEARCH_VALUES_ACTIONS.RESET_PROPERTY_TYPE:
      return { ...state, propertyType: null };
    case SEARCH_VALUES_ACTIONS.RESET_TAGS:
      return { ...state, tags: [] };
    case SEARCH_VALUES_ACTIONS.RESET_AMENITIES:
      return { ...state, amenities: [] };
    case SEARCH_VALUES_ACTIONS.RESET_NUMBER_OF_BEDROOMS:
      return { ...state, numberOfBedrooms: 0 };
    case SEARCH_VALUES_ACTIONS.RESET_NUMBER_OF_BATHROOMS:
      return { ...state, numberOfBathrooms: 0 };
    case SEARCH_VALUES_ACTIONS.CLEAR_ALL:
      return {
        ...state,
        tags: [],
        amenities: [],
        maxPrice: null,
        minPrice: null,
        numberOfBathrooms: 0,
        numberOfBedrooms: 0,
        propertyType: null,
      };
    default:
      return state;
  }
}

export function useSearchValuesForm() {
  const searchParams = useSearchValues();
  const [state, dispatch] = useReducer(reducer, initialState(searchParams));
  const [, isPriceFilterFixEnabled] = useFeatureToggle(PRICE_FILTER_FIX);
  const { defaultCurrency } = useContext(CurrencyContext);

  const getFilterParams = useCallback(() => {
    return {
      currency: defaultCurrency,
      tags: state.tags.toString(),
      includeAmenities: state.amenities.toString(),
      maxPrice: state.maxPrice,
      minPrice: state.minPrice,
      numberOfBathrooms: state.numberOfBathrooms,
      numberOfBedrooms: state.numberOfBedrooms,
      propertyType: state.propertyType,
    };
  }, [defaultCurrency, state]);

  return {
    searchParams,
    ...state,
    dispatch,
    isPriceFilterFixEnabled,
    getFilterParams,
  };
}

export default useSearchValues;
